import React from "react";
import { useT } from "@/lib/i18n";

// 4 家客户品牌官方裁切标准高清 Logo（与 UI 原型素材 100% 像素级一致）
const VOICES_DATA = [
  {
    id: "zdeer",
    name: "左点",
    nameEn: "zdeer",
    industry: "医药健康",
    industryEn: "Healthcare",
    logoUrl: "https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/c1af100f97b84e079887a8e56484d4b2.png",
    quote: "「与OranAI合作后，我们能针对不同平台和人群快速生成统一风格的产品视觉，让官网、电商和营销素材的更新更加顺畅高效。」",
    quoteEn: "Partnering with OranAI allows us to quickly generate consistent product visuals for different platforms and audiences, streamlining our official website, e-commerce, and marketing updates.",
  },
  {
    id: "matrix",
    name: "矩阵纵横",
    nameEn: "Matrix Design",
    industry: "空间设计",
    industryEn: "Spatial Design",
    logoUrl: "https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/109813e692364e3a89170703daf5dad0.png",
    quote: "「OranAI帮助我们补齐了数字资产底座，让品牌内容运营从分散存放真正走向了系统化管理。」",
    quoteEn: "OranAI helped us establish a solid digital asset foundation, taking our brand content operations from scattered storage to systematic, secure management.",
  },
  {
    id: "1more",
    name: "万魔",
    nameEn: "1MORE",
    industry: "智能声学",
    industryEn: "Smart Acoustics",
    logoUrl: "https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/e4d4bab919924fd2b23b208ddfd7bc7f.png",
    quote: "「与OranAI合作后，我们能更快生成适配不同社媒平台的视觉素材，让音质、佩戴感与生活方式氛围的传达更加自然真实。」",
    quoteEn: "With OranAI, we generate tailored visual assets for social platforms much faster, communicating sound quality, wearing comfort, and lifestyle atmosphere with authentic realism.",
  },
  {
    id: "timekettle",
    name: "时空壶",
    nameEn: "Timekettle",
    industry: "AI 同传",
    industryEn: "AI Translation",
    logoUrl: "https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/f6831ee4ca5f46ef89fcf095b2425e4c.png",
    quote: "「OranAI帮助我们建立了统一的视觉与短视频表达体系，让产品在多渠道海外传播中始终保持一致、专业且易于理解。」",
    quoteEn: "OranAI helped us build a unified visual and short-video messaging system, ensuring consistent, professional, and accessible brand representation across all overseas channels.",
  },
];

/**
 * S4b · VOICES（卡片滚动播放 + Logo 占位）
 * 100% 采用 Tailwind CSS 工具类与 i18n 多语言体系，无缝跑马灯 + 悬停暂停
 */
export const VoicesMarqueeSection: React.FC = () => {
  const t = useT();

  return (
    <section id="voices" className="relative pb-24 sm:pb-28 overflow-hidden bg-[#0B0907]">
      {/* 标题区域（左对齐，与 UI 素材一致） */}
      <div className="max-w-[1400px] mx-auto px-6 mb-8 sm:mb-10">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#F2ECE5] leading-[1.15] text-left">
          <span>{t("Voices of Partners & ", "客户与创作者的")}</span>
          <span className="text-[#F97316] font-extrabold">{t("Voices", "声音")}</span>
        </h2>
      </div>

      {/* 跑马灯滚动视口（两端线性羽化遮罩 + 悬停暂停） */}
      <div
        className="relative overflow-hidden group"
        style={{
          maskImage: "linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)",
        }}
        aria-label={t("Partner Voices Marquee", "客户评价滚动")}
      >
        <div className="flex gap-4 sm:gap-5 w-max animate-marquee py-3 group-hover:[animation-play-state:paused]">
          {/* 拼接三份以确保大屏超宽视口下的绝对平滑无缝 */}
          {[...VOICES_DATA, ...VOICES_DATA, ...VOICES_DATA].map((item, index) => (
            <article
              key={`${item.id}-${index}`}
              className="w-[380px] flex-none flex flex-col gap-4 p-6 sm:p-7 rounded-[18px] bg-[#120E0A] border border-[#F97316]/22 hover:border-[#F97316] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_18px_50px_rgba(249,115,22,0.16)] select-none will-change-transform"
            >
              {/* 头部：严格对齐 126px*44px 标准 Logo 容器 + 品牌/行业 */}
              <div className="flex items-center gap-3.5">
                <div className="w-[126px] h-[44px] inline-flex items-center justify-center shrink-0">
                  <img
                    src={item.logoUrl}
                    alt={`${item.name} Logo`}
                    className="w-full h-full max-w-[126px] max-h-[44px] object-contain object-center block"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col gap-0.5 text-left">
                  <b className="text-base font-bold text-[#F2ECE5] leading-tight">
                    {t(item.nameEn, item.name)}
                  </b>
                  <span className="text-sm sm:text-base font-bold text-[#F97316]/75 tracking-normal">
                    {t(item.industryEn, item.industry)}
                  </span>
                </div>
              </div>

              {/* 引用评价文字 */}
              <p className="text-[14.5px] sm:text-[15px] font-medium leading-[1.8] text-[#F2ECE5]/85 text-left flex-1">
                {t(item.quoteEn, item.quote)}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VoicesMarqueeSection;
